import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Button,
  Alert,
  Chip,
  MenuItem,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import BrandForm from "./BrandForm";

const Brands = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Brands");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Brands_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const columns = [
    { title: "ID", field: "id" },
    { title: "Brand ID", field: "brand_id" },
    { title: "Org ID", field: "org_id" },
    { title: "Name", field: "name" },
    { title: "Segment", field: "segment" },
    {
      title: "Description",
      field: "description",
      render: (rowData) => (
        <div
          style={{
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rowData.description}
        </div>
      ),
    },
    { title: "Logo URL", field: "logo_url" },
    {
      title: "Style Guide",
      field: "style_guide",
      render: (rowData) => (
        <div
          style={{
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {typeof rowData.style_guide === "object"
            ? JSON.stringify(rowData.style_guide)
            : rowData.style_guide}
        </div>
      ),
    },
    {
      title: "Status",
      field: "is_active",
      render: (rowData) => (
        <Chip
          label={rowData.is_active ? "Active" : "Inactive"}
          color={rowData.is_active ? "success" : "error"}
          size="small"
        />
      ),
    },
  ];

  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/HotelBrand");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format.");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editData?.id) {
        const payload = {
          data: {
            ...formData,
            brand_id: parseInt(formData.brand_id),
            org_id: parseInt(formData.org_id),
            style_guide: formData.style_guide
              ? JSON.parse(formData.style_guide)
              : null,
          },
        };

        const response = await fetch(
          `https://api.esthell.com/HotelBrand/${editData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update brand");
        }
        setNotification({
          open: true,
          message: "Brand updated successfully!",
          type: "success",
        });
      } else {
        // Generate brand_id automatically
        const numberingPayload = {
          warehouse_id: "WH001",
          numbering_type: "BRD",
          gen_year: new Date().getFullYear(),
        };

        const numberingResponse = await fetch(
          "https://api.esthell.com/location_numbertype_mst",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(numberingPayload),
          },
        );

        if (!numberingResponse.ok) {
          throw new Error("Failed to generate brand ID");
        }

        const numberingData = await numberingResponse.json();
        const nextBrandId = parseInt(numberingData[0].last_gen_number) + 1;

        // Create brand with generated brand_id
        const payload = {
          data: {
            ...formData,
            brand_id: nextBrandId,
            org_id: parseInt(formData.org_id),
            style_guide: formData.style_guide
              ? JSON.parse(formData.style_guide)
              : null,
          },
        };

        const response = await fetch(
          "https://api.esthell.com/create/HotelBrand",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create brand");
        }
        setNotification({
          open: true,
          message: "Brand created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const response = await fetch(
        `https://api.esthell.com/HotelBrand/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete brand");
      }

      fetchData();
      setNotification({
        open: true,
        message: "Brand deleted successfully!",
        type: "success",
      });
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotel Brands
        </Typography>
        <Button variant="outlined" onClick={handleExportToExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <BrandForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            handleSubmit={handleFormSubmit}
            initialData={editData}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Brands;
